import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}`;

const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('tayane_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            localStorage.removeItem('tayane_token');
            localStorage.removeItem('tayane_user');
            window.location.href = '/login';
        }
        return Promise.reject(err);
    }
);

// Auth
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
};

// Leads
export const leadsAPI = {
    list: (params) => api.get('/admin/leads', { params }),
    get: (id) => api.get(`/admin/leads/${id}`),
    create: (data) => api.post('/admin/leads', data),
    update: (id, data) => api.put(`/admin/leads/${id}`, data),
    delete: (id) => api.delete(`/admin/leads/${id}`),
    addHistory: (id, data) => api.post(`/admin/leads/${id}/history`, data),
    subscribe: (data) => api.post('/leads/subscribe', data),
};

// Eventos
export const eventsAPI = {
    listPublic: () => api.get('/events'),
    list: () => api.get('/admin/events'),
    create: (data) => api.post('/admin/events', data),
    update: (id, data) => api.put(`/admin/events/${id}`, data),
    delete: (id) => api.delete(`/admin/events/${id}`),
    register: (id, data) => api.post(`/events/${id}/register`, data),
    heatmap: () => api.get('/admin/events/heatmap'),
    stats: () => api.get('/admin/events/stats'),
};

// Waitlist
export const waitlistAPI = {
    subscribe: (data) => api.post('/waitlist', data),
    list: (params) => api.get('/admin/waitlist', { params }),
    updateStatus: (id, status) => api.patch(`/admin/waitlist/${id}/status?status=${status}`),
};

// Followup Rules
export const followupAPI = {
    list: () => api.get('/admin/followup-rules'),
    create: (data) => api.post('/admin/followup-rules', data),
    update: (id, data) => api.put(`/admin/followup-rules/${id}`, data),
    delete: (id) => api.delete(`/admin/followup-rules/${id}`),
};

// Campaigns
export const campaignsAPI = {
    list: () => api.get('/admin/campaigns'),
    create: (data) => api.post('/admin/campaigns', data),
    update: (id, data) => api.put(`/admin/campaigns/${id}`, data),
    send: (id) => api.post(`/admin/campaigns/${id}/send`),
    delete: (id) => api.delete(`/admin/campaigns/${id}`),
};

// Flows
export const flowsAPI = {
    list: () => api.get('/admin/flows'),
    get: (id) => api.get(`/admin/flows/${id}`),
    create: (data) => api.post('/admin/flows', data),
    update: (id, data) => api.put(`/admin/flows/${id}`, data),
    delete: (id) => api.delete(`/admin/flows/${id}`),
    activate: (id) => api.post(`/admin/flows/${id}/activate`),
    deactivate: (id) => api.post(`/admin/flows/${id}/deactivate`),
};

// Email Templates
export const templatesAPI = {
    list: () => api.get('/admin/email-templates'),
    get: (id) => api.get(`/admin/email-templates/${id}`),
    create: (data) => api.post('/admin/email-templates', data),
    update: (id, data) => api.put(`/admin/email-templates/${id}`, data),
    delete: (id) => api.delete(`/admin/email-templates/${id}`),
};

// WhatsApp
export const whatsappAPI = {
    status: () => api.get('/admin/whatsapp/status'),
    listTemplates: () => api.get('/admin/whatsapp/templates'),
    createTemplate: (data) => api.post('/admin/whatsapp/templates', data),
    updateTemplate: (id, data) => api.put(`/admin/whatsapp/templates/${id}`, data),
    deleteTemplate: (id) => api.delete(`/admin/whatsapp/templates/${id}`),
    send: (data) => api.post('/admin/whatsapp/send', data),
    conversations: () => api.get('/admin/whatsapp/conversations'),
    messages: (jid) => api.get(`/admin/whatsapp/conversations/${jid}/messages`),
};

// Estoque
export const stockAPI = {
    categories: () => api.get('/admin/stock/categories'),
    createCategory: (data) => api.post('/admin/stock/categories', data),
    products: (params) => api.get('/admin/stock/products', { params }),
    getProduct: (id) => api.get(`/admin/stock/products/${id}`),
    createProduct: (data) => api.post('/admin/stock/products', data),
    updateProduct: (id, data) => api.put(`/admin/stock/products/${id}`, data),
    movements: (params) => api.get('/admin/stock/movements', { params }),
    entry: (data) => api.post('/admin/stock/movements/entry', data),
    exit: (data) => api.post('/admin/stock/movements/exit', data),
    alerts: () => api.get('/admin/stock/alerts'),
    summary: () => api.get('/admin/stock/summary'),
};

// Pagamentos
export const ordersAPI = {
    list: (params) => api.get('/admin/orders', { params }),
    checkout: (data) => api.post('/payments/checkout', data),
};

// Blog
export const blogAPI = {
    listPublic: (params) => api.get('/blog', { params }),
    getPublic: (slug) => api.get(`/blog/${slug}`),
    list: () => api.get('/admin/blog'),
    create: (data) => api.post('/admin/blog', data),
    update: (id, data) => api.put(`/admin/blog/${id}`, data),
    delete: (id) => api.delete(`/admin/blog/${id}`),
    aiGenerate: (params) => api.post('/admin/blog/ai-generate', null, { params }),
};

// Brand Voice
export const brandVoiceAPI = {
    get: () => api.get('/admin/brand-voice'),
    upsert: (data) => api.put('/admin/brand-voice', data),
};

// Galeria
export const galleryAPI = {
    listPublic: (params) => api.get('/gallery', { params }),
    list: () => api.get('/admin/gallery'),
    create: (data) => api.post('/admin/gallery', data),
    delete: (id) => api.delete(`/admin/gallery/${id}`),
};

// Testemunhos
export const testimonialsAPI = {
    listPublic: (params) => api.get('/testimonials', { params }),
    list: () => api.get('/admin/testimonials'),
    create: (data) => api.post('/admin/testimonials', data),
    update: (id, data) => api.put(`/admin/testimonials/${id}`, data),
    delete: (id) => api.delete(`/admin/testimonials/${id}`),
};

// Menções
export const mentionsAPI = {
    list: (params) => api.get('/admin/mentions', { params }),
    create: (data) => api.post('/admin/mentions', data),
    stats: () => api.get('/admin/mentions/stats'),
    delete: (id) => api.delete(`/admin/mentions/${id}`),
};

// KPIs
export const kpisAPI = {
    list: (params) => api.get('/admin/kpis', { params }),
    latest: () => api.get('/admin/kpis/latest'),
};

// Visitors
export const visitorsAPI = {
    track: (data) => api.post('/visitors/track', data),
    convert: (data) => api.post('/visitors/convert', data),
    stats: () => api.get('/admin/visitors/stats'),
    list: (params) => api.get('/admin/visitors', { params }),
    byRegion: () => api.get('/admin/visitors/by-region'),
};

// Contato
export const contactAPI = {
    send: (data) => api.post('/contact', data),
    list: (params) => api.get('/admin/contacts', { params }),
    updateStatus: (id, status) => api.put(`/admin/contacts/${id}/status?status=${status}`),
};

// Users
export const usersAPI = {
    list: () => api.get('/admin/users'),
    create: (data) => api.post('/admin/users', data),
    update: (id, data) => api.put(`/admin/users/${id}`, data),
    delete: (id) => api.delete(`/admin/users/${id}`),
};

// Stats
export const statsAPI = {
    dashboard: () => api.get('/admin/stats'),
    visitors: () => api.get('/admin/visitors/stats'),
    mentions: () => api.get('/admin/mentions/stats'),
    kpisLatest: () => api.get('/admin/kpis/latest'),
    stockSummary: () => api.get('/admin/stock/summary'),
};

// Upload
export const uploadAPI = {
    image: (formData) => api.post('/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Activity Log
export const activityAPI = {
    list: (params) => api.get('/admin/activity-logs', { params }),
};

// Backups
export const backupsAPI = {
    list: () => api.get('/admin/backups'),
    trigger: () => api.post('/admin/backups/trigger'),
};

// Gerador de roteiros (usa AI generate do blog)
export const roteiroAPI = {
    gerar: (params) => api.post('/admin/blog/ai-generate', null, { params }),
};

export default api;

// Named exports for compatibility
export { api };
export const API_BASE = API_URL;
export const formatApiError = (err) => {
    if (err?.response?.data?.detail) return String(err.response.data.detail);
    if (err?.response?.data?.message) return String(err.response.data.message);
    if (err?.message) return err.message;
    return 'Erro desconhecido';
};

// Constants
export const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '5511999999999';
export const WHATSAPP_LINK = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '5511999999999'}`;

// Perfil (configurações do site)
export const perfilAPI = {
    get: () => api.get('/admin/perfil'),
    update: (data) => api.put('/admin/perfil', data),
};

// Kanban (eleitores/leads por status)
export const kanbanAPI = {
    get: () => api.get('/admin/kanban'),
    move: (data) => api.post('/admin/kanban/move', data),
};
